import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { PencilIcon, PrinterIcon } from "@heroicons/react/24/outline";
import { HsePageChrome, useHseBusyAction } from "../../../../../components/dashboard/hse/HsePageChrome";
import { ResourceError } from "../../../../../components/utils/ResourceError";
import { ResourceLoading } from "../../../../../components/utils/ResourceLoading";
import { PMDialog } from "../../../../../components/utils/PMDialog";
import { errorStateVariantFromError } from "../../../../../components/utils/errorStateUtils";
import { useUserHasAccess } from "../../../../../routes/roleOnly";
import {
  exportHseSafetyInspectionPdf,
  getHseSafetyInspection,
} from "../../../../../api/hse/safetyInspections";
import { HsePageBody } from "../../../../../components/dashboard/hse/HsePageBody";
import { HseSafetyInspectionForm } from "../../../../../components/dashboard/hse/hseSafetyInspectionForm";
import { HseSafetyInspectionSummary } from "../../../../../components/dashboard/hse/hseSafetyInspectionSummary";
import { RiskSubnav } from "../../../../../components/dashboard/hse/risk/RiskSubnav";

function canEditInspection(inspection) {
  if (!inspection) return false;
  if (typeof inspection.can_be_edited === "boolean") return inspection.can_be_edited;
  return ["draft", "in_progress"].includes(inspection.status);
}

export const HseKsProtectiveEquipmentView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const canEditRole = useUserHasAccess({ role: "hse_ks_risk", action: "edit" });
  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorVariant, setErrorVariant] = useState("load_error");
  const [showForm, setShowForm] = useState(false);
  const { run, busyProps } = useHseBusyAction();

  const load = async () => {
    try {
      setLoading(true);
      setError(false);
      const response = await getHseSafetyInspection(id);
      setInspection(response.data?.data || response.data);
    } catch (err) {
      console.error(err);
      setError(true);
      setErrorVariant(errorStateVariantFromError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const handleExportPdf = () =>
    run("pdf", async () => {
      try {
        await exportHseSafetyInspectionPdf(id, `hse-vernerunde-${id}.pdf`);
        toast.success(t("hse.protective.export_pdf_success"));
      } catch (err) {
        toast.error(err.message || t("hse.protective.export_pdf_error"));
      }
    });

  const handleFormSubmit = (saved) => {
    setInspection(saved);
    setShowForm(false);
    load();
  };

  const canEdit = canEditRole && canEditInspection(inspection);

  return (
    <>
      <HsePageChrome
        title={inspection?.title || t("hse.protective.view_title")}
        subtitle={t("hse.protective.view_subtitle")}
        backTo="/dashboard/hse-ks/risk/protective-equipment"
        mainButton={{
          label: t("hse.protective.edit"),
          action: () => setShowForm(true),
          icon: PencilIcon,
          hidden: loading || error || !canEdit,
        }}
        actions={[
          {
            label: t("hse.protective.export_pdf"),
            action: handleExportPdf,
            icon: PrinterIcon,
            hidden: loading || error || !inspection,
            menu: false,
            ...busyProps("pdf"),
          },
        ]}
      >
        <HsePageBody>
          <RiskSubnav active="protective-equipment" />
          {loading ? (
            <div className="mt-6">
              <ResourceLoading />
            </div>
          ) : error || !inspection ? (
            <div className="mt-6">
              <ResourceError
                variant={!inspection && !error ? "not_found" : errorVariant}
                errors={
                  errorVariant === "load_error" && error
                    ? [t("hse.protective.load_failed")]
                    : undefined
                }
                onRetry={error ? load : undefined}
                backTo="/dashboard/hse-ks/risk/protective-equipment"
              />
            </div>
          ) : (
            <div className="mt-6 space-y-6">
              <div className="rounded-[18px] border border-slate-200 bg-white p-5 shadow-sm">
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {t("hse.protective.col_status")}
                    </dt>
                    <dd className="mt-1 text-sm text-slate-900">
                      {t(`hse.protective.status.${inspection.status}`, {
                        defaultValue: inspection.status_display || inspection.status,
                      })}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {t("hse.protective.col_date")}
                    </dt>
                    <dd className="mt-1 text-sm text-slate-900">
                      {inspection.inspection_date
                        ? new Date(inspection.inspection_date).toLocaleDateString()
                        : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {t("hse.protective.col_location")}
                    </dt>
                    <dd className="mt-1 text-sm text-slate-900">
                      {inspection.location || "—"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {t("hse.protective.field.registered_by")}
                    </dt>
                    <dd className="mt-1 text-sm text-slate-900">
                      {inspection.registered_by?.displayTitle ||
                        inspection.registered_by?.name ||
                        "—"}
                    </dd>
                  </div>
                </dl>
                {inspection.description ? (
                  <div className="mt-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {t("hse.protective.field.description")}
                    </p>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">
                      {inspection.description}
                    </p>
                  </div>
                ) : null}
              </div>
              <HseSafetyInspectionSummary
                inspection={inspection}
                showClose={false}
                showHeader={false}
              />
            </div>
          )}
        </HsePageBody>
      </HsePageChrome>

      {showForm && inspection ? (
        <PMDialog
          open={showForm}
          onClose={() => setShowForm(false)}
          title={t("hse.protective.edit")}
          size="7xl"
        >
          <HseSafetyInspectionForm
            inspection={inspection}
            onSubmit={handleFormSubmit}
            onCancel={() => setShowForm(false)}
          />
        </PMDialog>
      ) : null}
    </>
  );
};
